import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { DatePicker } from '../ui/date-picker';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  FileText, DollarSign, CheckCircle2, Trash2, CloudUpload, AlertTriangle, RefreshCw, Loader2, ReceiptText,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';
import { format } from 'date-fns';

/**
 * DryHireInvoicingPanel
 * ─────────────────────
 * Drawer panel that manages a dry-hire job's invoice ledger:
 *   • Live preview of the next partial invoice (full cycles only)
 *   • Generate partial or final drafts
 *   • Finalise a draft → optionally syncs to the tenant's accounting integration
 *   • Delete a draft (rewinds last_invoiced_to automatically)
 */
export function DryHireInvoicingPanel({ api, jobId }) {
  const [invoices, setInvoices] = useState([]);
  const [preview, setPreview] = useState(null);
  const [finalPreview, setFinalPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [generateOpen, setGenerateOpen] = useState(false);
  const [upToDate, setUpToDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const refresh = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const [invResp, pvResp, finalResp] = await Promise.all([
        api.get(`/dry-hires/${jobId}/invoices`),
        api.get(`/dry-hires/${jobId}/pricing-preview?mode=full_cycles&up_to_date=${upToDate}`),
        api.get(`/dry-hires/${jobId}/pricing-preview?mode=blend`),
      ]);
      setInvoices(invResp.data?.items || []);
      setPreview(pvResp.data);
      setFinalPreview(finalResp.data);
    } catch (e) {
      // Non-fatal — missing rates show in preview.warnings.
      console.warn('invoicing refresh failed', e);
    } finally {
      setLoading(false);
    }
  }, [api, jobId, upToDate]);

  useEffect(() => { refresh(); }, [refresh]);

  const generatePartial = async () => {
    setBusy(true);
    try {
      await api.post(`/dry-hires/${jobId}/partial-invoice`, { up_to_date: upToDate });
      toast.success('Partial invoice created');
      setGenerateOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate');
    } finally { setBusy(false); }
  };

  const generateFinal = async () => {
    setConfirmDialog({
      open: true,
      title: 'Generate Final Invoice',
      description: 'Generate a FINAL invoice for the remaining period? This closes the billing ledger for this hire.',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setBusy(true);
        try {
          await api.post(`/dry-hires/${jobId}/final-invoice`, {});
          toast.success('Final invoice created');
          await refresh();
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Failed to generate');
        } finally { setBusy(false); }
      },
    });
  };

  const finalise = async (inv) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dry-hires/invoices/${inv.id}/finalise`);
      if (data.synced_invoice_id) {
        toast.success(`Finalised and synced to ${data.synced_provider || 'accounting'}`);
      } else if (data.sync_error) {
        const fix = data.sync_suggested_fix ? ` — ${data.sync_suggested_fix}` : '';
        toast.warning(`Finalised locally. ${data.sync_provider_attempted || 'Accounting'} sync failed: ${data.sync_error}${fix}`, {
          duration: 12000,
        });
      } else {
        toast.success('Invoice finalised (accounting not connected)');
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalise failed');
    } finally { setBusy(false); }
  };

  const retrySync = async (inv) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dry-hires/invoices/${inv.id}/push-to-accounting`);
      toast.success(`Synced to ${data.provider}: ${data.invoice_number || data.invoice_id}`);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const fix = detail.suggested_fix ? `\n${detail.suggested_fix}` : '';
        toast.error(`${detail.platform || 'Accounting'} sync failed: ${detail.summary || detail.detail}${fix}`, {
          duration: 12000,
        });
      } else {
        toast.error(detail || 'Sync failed');
      }
    } finally { setBusy(false); }
  };

  const deleteDraft = async (inv) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Draft Invoice',
      description: 'Delete this draft invoice? The billing window will be re-opened.',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setBusy(true);
        try {
          await api.delete(`/dry-hires/invoices/${inv.id}`);
          toast.success('Draft deleted');
          await refresh();
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Delete failed');
        } finally { setBusy(false); }
      },
    });
  };

  const previewSubtotal = preview?.subtotal ?? 0;
  const previewBillable = preview?.chargeable_days ?? 0;
  const previewLeftover = preview?.leftover_days ?? 0;
  const previewWarnings = preview?.warnings || [];
  const priceListSource = preview?.price_list_source;

  const totalInvoiced = useMemo(
    () => invoices.reduce((sum, i) => sum + (i.subtotal || 0), 0),
    [invoices],
  );

  return (
    <>
    <div className="space-y-3 p-3 rounded-md border border-zinc-800 bg-zinc-900/30" data-testid="dryhire-invoicing-panel">
      <div className="flex items-center justify-between">
        <Label className="label-overline flex items-center gap-2">
          <ReceiptText className="h-4 w-4" />
          Invoicing
        </Label>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            Invoiced to date: <span className="text-foreground font-medium">${totalInvoiced.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={refresh} disabled={loading || busy} data-testid="dryhire-invoicing-refresh-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Preview strip */}
      <div className="rounded-md bg-zinc-950/50 border border-zinc-800 p-3 text-sm" data-testid="dryhire-invoice-preview">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Next partial invoice · full cycles only
            </div>
            {preview?.line_items?.length ? (
              <div className="space-y-0.5">
                {preview.line_items.map((li, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-muted-foreground truncate">{li.qty} × {li.uom.toLowerCase()} @ ${li.rate}</span>
                    <span className="font-mono">${li.amount.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-zinc-800 pt-1 mt-1">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-mono font-bold text-[#9aff00]">${previewSubtotal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                </div>
                {previewLeftover > 0 && (
                  <div className="text-[11px] text-muted-foreground pt-0.5">
                    {previewLeftover} day{previewLeftover===1?'':'s'} will carry forward to the next cycle.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground text-xs">
                {previewBillable === 0 ? 'No unbilled days yet.' : 'Not enough days for a full cycle — wait or switch to daily unit.'}
              </div>
            )}
            {previewWarnings.map((w, i) => (
              <div key={i} className="flex items-center gap-1 text-[11px] text-amber-400">
                <AlertTriangle className="h-3 w-3 shrink-0" /> {w}
              </div>
            ))}
            {priceListSource && (
              <div className="text-[11px] text-muted-foreground">
                Rate source: <span className="text-foreground">{preview?.price_list_name || '—'}</span>
                {priceListSource === 'default' && <span className="opacity-60"> (tenant default)</span>}
                {priceListSource === 'customer' && <span className="opacity-60"> (customer price list)</span>}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Button size="sm" className="btn-volt" disabled={busy || !preview?.line_items?.length} onClick={() => setGenerateOpen(true)} data-testid="dryhire-generate-partial-btn">
              <FileText className="h-3.5 w-3.5 mr-1" />
              Generate Partial
            </Button>
            {finalPreview?.line_items?.length > 0 && (
              <Button size="sm" variant="outline" disabled={busy} onClick={generateFinal} data-testid="dryhire-generate-final-btn">
                <DollarSign className="h-3.5 w-3.5 mr-1" />
                Generate Final ${finalPreview.subtotal.toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Ledger */}
      {invoices.length > 0 && (
        <div className="rounded-md border border-zinc-800 overflow-hidden" data-testid="dryhire-invoice-ledger">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-1.5">Type</th>
                <th className="text-left px-3 py-1.5">Window</th>
                <th className="text-right px-3 py-1.5">Amount</th>
                <th className="text-center px-3 py-1.5">Status</th>
                <th className="text-right px-3 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-t border-zinc-800" data-testid={`dryhire-invoice-row-${inv.id}`}>
                  <td className="px-3 py-1.5">
                    <Badge variant="outline" className={`text-[10px] ${inv.invoice_type === 'final' ? 'border-[#9aff00]/40 text-[#9aff00]' : 'border-sky-500/40 text-sky-400'}`}>
                      {inv.invoice_type}
                    </Badge>
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">
                    {inv.window_start} → {inv.billed_through}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    ${(inv.subtotal || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {inv.status === 'draft' && <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-300">Draft</Badge>}
                    {inv.status === 'finalised' && inv.synced_invoice_id && (
                      <Badge variant="outline" className="text-[10px] border-[#9aff00]/40 text-[#9aff00] gap-1" title={`Synced to ${inv.synced_provider}`}>
                        <CloudUpload className="h-2.5 w-2.5" /> Synced
                      </Badge>
                    )}
                    {inv.status === 'finalised' && !inv.synced_invoice_id && (
                      <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-400" title={inv.sync_error || 'Accounting not connected'}>
                        Finalised
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right space-x-1">
                    {inv.status === 'draft' && (
                      <>
                        <Button size="icon" variant="ghost" className="h-6 w-6" disabled={busy} onClick={() => finalise(inv)} title="Finalise & Sync" data-testid={`dryhire-invoice-finalise-${inv.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#9aff00]" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" disabled={busy} onClick={() => deleteDraft(inv)} title="Delete draft" data-testid={`dryhire-invoice-delete-${inv.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </>
                    )}
                    {inv.status === 'finalised' && !inv.synced_invoice_id && (
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={busy} onClick={() => retrySync(inv)} title={inv.sync_error ? `Retry sync — ${inv.sync_error}` : 'Push to accounting'} data-testid={`dryhire-invoice-retry-${inv.id}`}>
                        <CloudUpload className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Partial-invoice generator dialog */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent data-testid="dryhire-generate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate Partial Invoice</DialogTitle>
            <DialogDescription className="text-xs">
              Bills complete cycles only. Leftover days roll into the next invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Bill up to</Label>
              <DatePicker
                value={upToDate}
                minDate={invoices[0]?.billed_through || undefined}
                onChange={(date) => setUpToDate(date || '')}
                testId="dryhire-up-to-date-input"
                className="mt-1 w-full"
              />
            </div>
            <div className="rounded-md bg-zinc-950/50 border border-zinc-800 p-3 text-sm space-y-1">
              {preview?.line_items?.map((li, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-muted-foreground">{li.qty} × {li.uom.toLowerCase()} @ ${li.rate}</span>
                  <span className="font-mono">${li.amount.toFixed(2)}</span>
                </div>
              )) || <div className="text-muted-foreground text-xs">Select a date to preview.</div>}
              {preview?.line_items?.length > 0 && (
                <div className="flex justify-between border-t border-zinc-800 pt-1 mt-1">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-mono font-bold text-[#9aff00]">${previewSubtotal.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGenerateOpen(false)} disabled={busy}>Cancel</Button>
            <Button className="btn-volt" disabled={busy || !preview?.line_items?.length} onClick={generatePartial} data-testid="dryhire-generate-confirm-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Confirm"
      />
    </>
  );
}

export default DryHireInvoicingPanel;
